import { useCallback, useEffect, useImperativeHandle, useState } from "react";

export interface UseSearchNavigationProps<T extends { id: string }> {
    items: T[];
    onSelect: (item: T) => void;
    ref?: any;
}

export interface UseSearchNavigationReturn {
    selectedIndex: number;
    handleKeydown: (event: KeyboardEvent | React.KeyboardEvent) => void;
    upHandler: () => void;
    downHandler: () => void;
    enterHandler: () => void;
    selectItem: (index: number) => void;
}

export function useSearchNavigation<T extends { id: string }>({ items, onSelect, ref }: UseSearchNavigationProps<T>): UseSearchNavigationReturn {
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const getItem = useCallback(
        (index: number): T | undefined => {
            if (!items || index < 0 || index >= items.length) {
                return undefined;
            }
            return items[index];
        },
        [items]
    );

    const scrollElementIntoView = useCallback(
        (index: number) => {
            const item = getItem(index);
            if (item) {
                const element = document.getElementById(item.id);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
            }
        },
        [getItem]
    );

    const upHandler = useCallback(() => {
        const newIndex = (selectedIndex + items.length - 1) % items.length;
        setSelectedIndex(newIndex);
        scrollElementIntoView(newIndex);
    }, [selectedIndex, items.length, scrollElementIntoView]);

    const downHandler = useCallback(() => {
        const newIndex = (selectedIndex + 1) % items.length;
        setSelectedIndex(newIndex);
        scrollElementIntoView(newIndex);
    }, [selectedIndex, items.length, scrollElementIntoView]);

    const selectItem = useCallback(
        (index: number) => {
            if (index >= 0 && index < items.length) {
                const item = items[index];
                onSelect(item);
            }
        },
        [items, onSelect]
    );

    const enterHandler = useCallback(() => {
        selectItem(selectedIndex);
    }, [selectItem, selectedIndex]);

    const handleKeydown = useCallback(
        (event: KeyboardEvent | React.KeyboardEvent) => {
            switch (event.key) {
                case "ArrowUp":
                    upHandler();
                    event.preventDefault();
                    break;
                case "ArrowDown":
                    downHandler();
                    event.preventDefault();
                    break;
                case "Enter":
                    enterHandler();
                    event.preventDefault();
                    break;
            }
        },
        [upHandler, downHandler, enterHandler]
    );

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
            if (event.key === "ArrowUp") {
                upHandler();
                return true;
            }

            if (event.key === "ArrowDown") {
                downHandler();
                return true;
            }

            if (event.key === "Enter") {
                enterHandler();
                return true;
            }

            return false;
        },
    }));

    useEffect(() => {
        if (!ref) {
            const handleGlobalKeyPress = (event: KeyboardEvent) => {
                handleKeydown(event);
            };

            window.addEventListener("keydown", handleGlobalKeyPress);

            return () => {
                window.removeEventListener("keydown", handleGlobalKeyPress);
            };
        }
    }, [ref, selectedIndex, items, handleKeydown]);

    return {
        selectedIndex,
        handleKeydown,
        upHandler,
        downHandler,
        enterHandler,
        selectItem,
    };
}
